import formatDate from '@/utils/dateFormat';
import truncateText from '@/utils/truncateText';
import calculateConversationDuration from '@/utils/calculateConversationDuration';
import Markdown from '@/components/markdown/Markdown';
import useTranslation from '@/hooks/useTranslation';
import { AllTranscriptsProps } from '@/types';
import MetadataItem from './MetadataItem';

interface ConversationMetadataProps {
  conversation?: AllTranscriptsProps | null;
  totalMessages?: number;
  isExpanded?: boolean;
  onSummaryToggle?: (expanded: boolean) => void;
  maxSummaryWords?: number;
  className?: string;
}

export default function ConversationMetadata({
  conversation,
  totalMessages,
  isExpanded = false,
  onSummaryToggle,
  maxSummaryWords = 80,
  className = '',
}: ConversationMetadataProps) {
  const { lang } = useTranslation();

  const summary = conversation?.digest?.summary ?? '';

  return (
    <div
      className={`w-[500px] text-dark-text max-h-min md800:w-full overflow-y-auto bg-secondary-background shadow-sm border border-dark-text/10 rounded-lg p-4 ${className}`}
    >
      <div className="flex flex-col gap-3">
        <MetadataItem label={lang.get('msg.conversationID')}>
          {conversation?.conversation_id ?? conversation?._id}
        </MetadataItem>

        <MetadataItem label={lang.get('msg.createdAt')} condition={!!conversation?.createdAt}>
          {formatDate(conversation?.createdAt as string, 'DD/MMM/YYYY - hh:mm:ss A')}
        </MetadataItem>

        <MetadataItem label={lang.get('msg.status')} condition={!!conversation?.status}>
          <span className="capitalize">{conversation?.status}</span>
        </MetadataItem>

        <MetadataItem
          label="Completion time"
          condition={!!(conversation?.startedAt && conversation?.finishedAt)}
        >
          {calculateConversationDuration({
            startedAt: conversation?.startedAt as string,
            finishedAt: conversation?.finishedAt as string,
          })}
        </MetadataItem>

        <MetadataItem
          label={lang.get('msg.metadata')}
          condition={!!(conversation?._id && conversation?.metadata)}
        >
          <div className="capitalize">
            {Object?.entries(conversation?.metadata ?? {})?.map(([key, value]) => (
              <div className="flex font-medium whitespace-nowrap" key={key}>
                <p className="capitalize">{key}</p>:<p className="ml-1">{String(value)}</p>
              </div>
            ))}
          </div>
        </MetadataItem>

        <MetadataItem label={lang.get('msg.totalMessages')}>
          <span className="capitalize">{totalMessages}</span>
        </MetadataItem>

        <MetadataItem label="Conversation summary" condition={!!summary}>
          <div>
            <Markdown>
              {isExpanded ? summary : truncateText(summary as string, maxSummaryWords)}
            </Markdown>
            {(summary ?? '').split(' ').length > maxSummaryWords && onSummaryToggle && (
              <button
                type="button"
                className="ml-1 text-blue-500 hover:underline"
                onClick={() => onSummaryToggle(!isExpanded)}
              >
                {isExpanded ? 'Show less' : 'Show more'}
              </button>
            )}
          </div>
        </MetadataItem>
      </div>
    </div>
  );
}
